'use client';

import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { useSearchParams, usePathname } from 'next/navigation';
import PaginationControls from './PaginationControls';
import { BaseCarousel, PaginationType } from 'components/shared/Carousel/BaseCarousel';
import { useBreakpoint } from 'hooks/useBreakpoints';

const DEFAULT_ARTICLES_PER_PAGE = 9;
const DEFAULT_GRID_CLASS_NAME =
  'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-12 sm:gap-x-8 sm:gap-y-12 lg:gap-x-10 lg:gap-y-14';

export type ArticleGridPaginationType = 'numbered' | 'carousel';

interface ArticleGridPaginatedProps extends PropsWithChildren {
  articlesPerPage?: number;
  articlesPerPageMobile?: number;
  gridClassName?: string;
  paginationType?: ArticleGridPaginationType;
}

function buildUrl(pathname: string, params: URLSearchParams, page: number): string {
  const next = new URLSearchParams(params);
  if (page <= 1) {
    next.delete('page');
  } else {
    next.set('page', String(page));
  }
  const qs = next.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}

const ArticleGridPaginated = ({
  children,
  articlesPerPage = DEFAULT_ARTICLES_PER_PAGE,
  articlesPerPageMobile,
  gridClassName = DEFAULT_GRID_CLASS_NAME,
  paginationType = 'numbered',
}: ArticleGridPaginatedProps) => {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const [isClient, setIsClient] = useState(false);
  const { isAboveMd } = useBreakpoint('md');

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Use articlesPerPage during SSR to avoid hydration mismatch (useBreakpoint
  // returns a different value on server vs client). Only apply the responsive
  // articlesPerPageMobile after hydration.
  const effectivePerPage =
    isClient && articlesPerPageMobile != null && !isAboveMd ? articlesPerPageMobile : articlesPerPage;
  const allChildren = React.Children.toArray(children);

  const onPageChange = useCallback(
    (page: number) => {
      const url = buildUrl(pathname, searchParams, page);
      window.history.pushState(null, '', url);
      window.dispatchEvent(new PopStateEvent('popstate'));
    },
    [pathname, searchParams],
  );

  if (paginationType === 'carousel') {
    const pages: React.ReactNode[][] = [];
    for (let i = 0; i < allChildren.length; i += effectivePerPage) {
      pages.push(allChildren.slice(i, i + effectivePerPage));
    }

    // Lock the row count so partial last pages don't stretch to fill the carousel
    // slide height. Mobile uses 1 column, desktop (md+) uses 3 columns.
    const mobileRows = articlesPerPageMobile ?? articlesPerPage;
    const desktopRows = Math.ceil(articlesPerPage / 3);
    const gridRowsClass =
      ' [grid-template-rows:repeat(var(--grid-rows),minmax(0,1fr))] md:[grid-template-rows:repeat(var(--grid-rows-md),minmax(0,1fr))]';
    const gridRowsStyle = {
      '--grid-rows': mobileRows,
      '--grid-rows-md': desktopRows,
    } as React.CSSProperties;

    return (
      <BaseCarousel
        totalItems={pages.length}
        carouselClassName="relative -mx-6 sm:-mx-10 md:mx-0"
        carouselPaginationClassName="mt-12 px-6 sm:px-10 md:px-0"
        carouselContentClassName="w-full overflow-hidden"
        showPaginationThreshold={1}
        paginationType={PaginationType.Numeric}
        carouselOpts={{ align: 'start', slidesToScroll: 1, containScroll: false }}>
        <div className="grid grid-flow-col auto-cols-[85%] md:auto-cols-[100%] gap-x-4">
          {pages.map((page, i) => (
            <div key={i} className={`${gridClassName}${gridRowsClass} pl-6 sm:pl-10 md:pl-0`} style={gridRowsStyle}>
              {page}
            </div>
          ))}
        </div>
      </BaseCarousel>
    );
  }

  // Numbered pagination intentionally uses articlesPerPage (not effectivePerPage) so that
  // page boundaries stay consistent across viewports — the ?page= URL param must resolve
  // to the same slice regardless of screen size.
  const totalPages = Math.max(1, Math.ceil(allChildren.length / articlesPerPage));
  const rawPage = Number(searchParams.get('page')) || 1;
  const currentPage = Math.min(Math.max(1, rawPage), totalPages);

  const startIdx = (currentPage - 1) * articlesPerPage;
  const pageChildren = allChildren.slice(startIdx, startIdx + articlesPerPage);

  return (
    <div>
      <div className={gridClassName}>{pageChildren}</div>
      <PaginationControls currentPage={currentPage} totalPages={totalPages} onPageChange={onPageChange} />
    </div>
  );
};

export default ArticleGridPaginated;
